/**
 * Session bootstrap.
 *
 * The customer never sees a sign-up form. On first load the app takes an
 * anonymous Supabase session, which issues a real JWT with a real `auth.uid()`;
 * every order RLS policy then works with no special-casing, and the customer's
 * order history survives a reload because the session is persisted.
 *
 * The whole app is gated on this resolving, because an order cannot be placed
 * without a uid and a screen that renders a checkout button which always fails
 * would be worse than a brief splash.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session } from '@yummy/db';
import { ensureAnonymousSession, onAuthStateChange } from '@yummy/db';

import { supabase } from '../lib/supabase';
import { useLocale } from '@yummy/ui-rnr';

import { FullScreenMessage } from '../components/FullScreenMessage';
import { LoaderCircle, TriangleAlert } from 'lucide-react-native';


interface SessionState {
  session: Session;
  userId: string;
}

const SessionContext = createContext<SessionState | null>(null);

/**
 * Reads the active session.
 *
 * @returns The session and the customer's user id.
 * @throws Error when called outside {@link SessionProvider}.
 */
export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside SessionProvider');
  return value;
}

/**
 * Establishes and maintains the anonymous session.
 *
 * @param props Children, rendered only once a session exists.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const { t } = useLocale();
  const [state, setState] = useState<SessionState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const session = await ensureAnonymousSession(supabase);
        if (!cancelled) setState({ session, userId: session.user.id });
      } catch (cause) {
        if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  // A token refresh or a sign-out on another tab must be reflected here.
  useEffect(
    () =>
      onAuthStateChange(supabase, (session) => {
        if (session) setState({ session, userId: session.user.id });
      }),
    [],
  );

  if (error) {
    return (
      <FullScreenMessage
        icon={TriangleAlert}
        title={t('customer.menu.sessionFailed')}
        message={error}
        actionLabel={t('common.retry')}
        onAction={() => {
          setError(null);
          setAttempt((n) => n + 1);
        }}
      />
    );
  }

  if (!state) {
    return (
      <FullScreenMessage
        icon={LoaderCircle}
        title={t('customer.menu.sessionStarting')}
        message={t('common.justAMoment')}
      />
    );
  }

  return <SessionContext.Provider value={state}>{children}</SessionContext.Provider>;
}
